import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowSquareOut, CalendarCheck, ChatCircleText, EnvelopeSimple, FolderSimple, MagnifyingGlass, Note, NotePencil, Phone, Plus, Signature, Sparkle,
  Trash, UploadSimple, UsersThree, X, type Icon,
} from '@phosphor-icons/react';
import {
  Chip, ImmoButton, InnenKarte, KartenTitel, LeerZustand, PanelZeile, SegmentedControl, TextFeld, ToastPill, useToastPill,
} from '../../../../../components/immobilien/ui';
import { useIslandActions } from '../../../../../components/island/IslandProvider';
import { deDatum } from '../../../../../lib/immobilien/objektDetail';
import { useNutzerId } from '../../../../../lib/immobilien/useNutzer';
import { kautionInfo } from '../../../../../lib/immobilien/einheitDetail';
import { istTicketOffen } from '../../../../../lib/immobilien/tickets';
import { logAktivitaet, loescheNotiz, speichereNotiz } from '../../../../../lib/kontakte/api';
import { kiFakten } from '../../../../../lib/mieter/detail';
import { istGekuendigt } from '../../../../../lib/mieter/liste';
import { ladeMieterDokumentHoch, oeffneMieterDokument } from '../../../../../lib/mieter/mieterDokumente';
import { meldeMieterGeaendert } from '../../../../../lib/mieter/useMieterDetail';
import { normalisiereSuche } from '../../../../../lib/immobilien/suche';
import VertragFormular from '../../../vertraege/VertragFormular';
import { AktivitaetBereich } from '../../../objekte/gemeinsam/WeitereBereiche';
import { TicketsBereich } from '../../../objekte/gemeinsam/TicketsBereich';
import { TerminFormularKarte } from '../../../objekte/gemeinsam/TerminFormularKarte';
import { ZaehlerVerwaltung } from '../../../objekte/objekt/panels/ZaehlerPanel';
import { useMieterSeite } from '../MieterSeitenKontext';
import { IosSchalter } from '../../../../../components/ui/IosSchalter';
import { setzeMieterFreigabe } from '../../../../../components/dokumente/MieterFreigabe';

const GLAS: React.CSSProperties = { background: 'var(--glass)' };
const fehlerText = (e: unknown) => (e instanceof Error ? e.message : 'Unbekannter Fehler');
const ZEIT = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' });

// ── Aktivität (Screen 30) ──────────────────────────────────────────────────────
type LogTyp = 'anruf' | 'mail' | 'meeting' | 'notiz';
const LOG_TYPEN: readonly { key: LogTyp; label: string; icon: Icon }[] = [
  { key: 'anruf', label: 'Anruf', icon: Phone }, { key: 'mail', label: 'Mail', icon: EnvelopeSimple },
  { key: 'meeting', label: 'Meeting', icon: UsersThree }, { key: 'notiz', label: 'Notiz', icon: Note },
];

export function AktivitaetPanel() {
  const s = useMieterSeite();
  const m = s.daten.mieter;
  const [typ, setTyp] = useState<LogTyp>('anruf');
  const [text, setText] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [toast, zeige] = useToastPill();

  // „Schnell loggen“ wie auf der Kontaktseite: Anruf/Mail/Meeting als Aktivität, Notiz als Kontakt-Notiz
  const eintragen = async () => {
    const b = text.trim();
    if (!b) return;
    setLaeuft(true);
    try {
      if (typ === 'notiz') await speichereNotiz('mieter', m.id, b);
      else await logAktivitaet('mieter', m.id, { typ, betreff: b, richtung: typ === 'meeting' ? null : 'ausgehend' });
      setText('');
      zeige('Eingetragen');
      meldeMieterGeaendert(m.id);
    } catch (err) {
      zeige(`Nicht eingetragen: ${fehlerText(err)}`, true);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte style={GLAS}>
        <KartenTitel>Schnell loggen</KartenTitel>
        <SegmentedControl ariaLabel="Art" hoehe={34} className="self-start" wert={typ} onChange={setTyp} segmente={LOG_TYPEN.map(t => ({ key: t.key, label: t.label, icon: t.icon }))} />
        <form className="flex flex-col" style={{ gap: 12 }} onSubmit={ev => { ev.preventDefault(); void eintragen(); }}>
          <TextFeld label="Worum ging es?" icon={ChatCircleText} wert={text} onChange={setText} placeholder="Kurzbeschreibung" maxLength={300} />
          <div className="flex justify-end">
            <ImmoButton type="submit" art="akzent" groesse={44} icon={Plus} disabled={laeuft || !text.trim()}>{laeuft ? 'Wird eingetragen …' : 'Eintragen'}</ImmoButton>
          </div>
        </form>
      </InnenKarte>
      <KartenTitel>Verlauf</KartenTitel>
      <AktivitaetBereich aktivitaeten={s.daten.aktivitaeten} hinweis={null} />
    </>
  );
}

// ── Verträge (Screen 31) ───────────────────────────────────────────────────────
export function VertraegePanel() {
  const s = useMieterSeite();
  const navigate = useNavigate();
  const nutzerId = useNutzerId();
  const m = s.daten.mieter;
  const [neu, setNeu] = useState(false);
  const [toast, zeige] = useToastPill();
  const v = s.daten.vertraege;
  return (
    <>
    {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
    {neu && (
      // Bestehendes Vertrags-Formular (gleiche Logik inkl. Frist-Terminen) im neuen Design direkt auf der Seite
      <div data-immo-ueberlagerung className="immo-side-in">
        <InnenKarte style={{ boxShadow: 'inset 0 0 0 2px var(--acc)' }}>
          <KartenTitel icon={Signature} rechts={
            <button type="button" aria-label="Schließen" onClick={() => setNeu(false)} className="flex items-center justify-center cursor-pointer"
              style={{ width: 30, height: 30, borderRadius: '50%', border: 'none', background: 'var(--fill2)', color: 'var(--ink)' }}><X size={14} weight="bold" /></button>
          }>Vertrag anlegen</KartenTitel>
          <VertragFormular variant="immo" editVertrag={null} userProfile={nutzerId ? { id: nutzerId } : undefined}
            prefillMieterId={m.id} prefillObjektId={m.objekt_id} prefillEinheit={s.einheit || null}
            onClose={() => setNeu(false)} onSaved={() => { setNeu(false); zeige('Vertrag angelegt'); meldeMieterGeaendert(m.id); }} />
        </InnenKarte>
      </div>
    )}
    <InnenKarte style={GLAS}>
      <KartenTitel rechts={!neu ? <ImmoButton groesse={36} icon={Plus} onClick={() => setNeu(true)}>Vertrag anlegen</ImmoButton> : undefined}>Verträge</KartenTitel>
      {v.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch keine Verträge mit diesem Mieter.</span>}
      <div className="flex flex-col">
        {v.map(x => {
          const aktiv = (x.status ?? '').toLowerCase() === 'aktiv';
          return (
            <button key={x.id} type="button" onClick={() => navigate(`/admin/vertraege/${encodeURIComponent(x.id)}`)} className="immo-hover-flaeche text-left cursor-pointer"
              style={{ border: 'none', background: 'transparent', borderRadius: 14, padding: 0, color: 'var(--ink)' }}>
              <PanelZeile icon={Signature} ton="acc" titel={[x.vertragsart ? `${x.vertragsart[0]?.toUpperCase() ?? ''}${x.vertragsart.slice(1)}` : 'Vertrag', x.vertragsnummer].filter(Boolean).join(' · ')}
                sub={x.unbefristet || !x.ende ? `unbefristet${x.beginn ? ` · seit ${deDatum(x.beginn)}` : ''}` : `${deDatum(x.beginn)} – ${deDatum(x.ende)}`}
                rechts={<Chip groesse="xs" ton={aktiv ? 'ok' : 'neutral'}>{x.status ? `${x.status[0]?.toUpperCase() ?? ''}${x.status.slice(1)}` : 'Ohne Status'}</Chip>} />
            </button>
          );
        })}
      </div>
    </InnenKarte>
    </>
  );
}

// ── Dokumente (Screen 32) ──────────────────────────────────────────────────────
export function DokumentePanel() {
  const s = useMieterSeite();
  const m = s.daten.mieter;
  const [suche, setSuche] = useState('');
  const [kategorie, setKategorie] = useState<string>('alle');
  const [laedtHoch, setLaedtHoch] = useState(false);
  const datei = useRef<HTMLInputElement>(null);
  const [toast, zeige] = useToastPill();
  const docs = s.daten.dokumente;
  const kategorien = useMemo(() => [...new Set(docs.map(d => (d.kategorie ?? '').trim() || 'Ohne Kategorie'))].sort((a, b) => a.localeCompare(b, 'de')), [docs]);
  const sichtbar = useMemo(() => {
    const q = normalisiereSuche(suche);
    return docs.filter(d => (kategorie === 'alle' || ((d.kategorie ?? '').trim() || 'Ohne Kategorie') === kategorie)
      && (!q || normalisiereSuche(`${d.titel ?? ''} ${d.datei_name ?? ''} ${d.kategorie ?? ''}`).includes(q)));
  }, [docs, suche, kategorie]);

  const hochladen = async (f: File) => {
    setLaedtHoch(true);
    try {
      await ladeMieterDokumentHoch({ datei: f, mieterId: m.id, mieterName: s.name, objektId: m.objekt_id, kategorie: 'Mieter', ordner: 'mieter' });
      zeige(`${f.name} hochgeladen`);
      meldeMieterGeaendert(m.id);
    } catch (err) {
      zeige(`Upload fehlgeschlagen: ${fehlerText(err)}`, true);
    } finally {
      setLaedtHoch(false);
      if (datei.current) datei.current.value = '';
    }
  };

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte style={GLAS}>
        <KartenTitel>Suchen</KartenTitel>
        <TextFeld label="Dokument" icon={MagnifyingGlass} wert={suche} onChange={setSuche} placeholder="Dokumente durchsuchen" />
        {kategorien.length > 1 && (
          <SegmentedControl ariaLabel="Kategorie" hoehe={30} className="self-start" wert={kategorie} onChange={setKategorie}
            segmente={[{ key: 'alle', label: 'Alle' }, ...kategorien.map(k => ({ key: k, label: k }))]} />
        )}
        <div className="flex justify-end">
          <input ref={datei} type="file" hidden onChange={e => { const f = e.target.files?.[0]; if (f) void hochladen(f); }} />
          <ImmoButton art="akzent" groesse={44} icon={UploadSimple} disabled={laedtHoch} onClick={() => datei.current?.click()}>{laedtHoch ? 'Wird hochgeladen …' : 'Hochladen'}</ImmoButton>
        </div>
      </InnenKarte>
      <InnenKarte style={GLAS}>
        <KartenTitel>{sichtbar.length} {sichtbar.length === 1 ? 'Dokument' : 'Dokumente'}</KartenTitel>
        {sichtbar.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>{docs.length ? 'Keine Treffer.' : 'Noch keine Dokumente zu diesem Mieter.'}</span>}
        <div className="flex flex-col">
          {sichtbar.map(d => (
            <div key={d.id} className="flex items-center" style={{ gap: 8 }}>
              <button type="button" className="immo-hover-flaeche text-left cursor-pointer min-w-0 flex-1" style={{ border: 'none', background: 'transparent', borderRadius: 14, padding: 0, color: 'var(--ink)' }}
                onClick={() => { void oeffneMieterDokument(d).then(ok => { if (!ok) zeige('Datei nur im Dokumentenbereich erreichbar', true); }); }}>
                <PanelZeile icon={FolderSimple} ton="acc" titel={d.titel || d.datei_name || 'Dokument'}
                  sub={[(d.kategorie ?? '').trim() || 'Ohne Kategorie', deDatum(d.created_at), d.fuer_mieter_sichtbar ? 'im Mieterportal' : ''].filter(Boolean).join(' · ')} rechts={<ArrowSquareOut size={15} color="var(--ink3)" />} />
              </button>
              {/* Portale E5: Häkchen „für Mieter freigeben“ – Standard aus, Admins und Team, nichts wird verschickt */}
              <IosSchalter klein an={!!d.fuer_mieter_sichtbar} label={`${d.titel || d.datei_name || 'Dokument'} für Mieter freigeben`}
                tip="Für Mieter freigeben – im Mieterportal zeigen (es wird nichts verschickt)"
                onChange={(an) => { void setzeMieterFreigabe(d.id, an).then(f => { if (f) { zeige(f, true); return; } zeige(an ? 'Für Mieter freigegeben' : 'Freigabe für Mieter entfernt'); meldeMieterGeaendert(m.id); }); }} />
            </div>
          ))}
        </div>
      </InnenKarte>
    </>
  );
}

// ── Termine (Screen 33) ────────────────────────────────────────────────────────
export function TerminePanel() {
  const s = useMieterSeite();
  const { onSearchNavigate } = useIslandActions();
  const navigate = useNavigate();
  const m = s.daten.mieter;
  const [neu, setNeu] = useState(false);
  const [toast, zeige] = useToastPill();
  const oeffne = (id: string) => { if (onSearchNavigate) onSearchNavigate('/admin/kalender', id, 'termin'); else navigate('/admin/kalender'); };
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      {neu ? (
        <TerminFormularKarte termin={null} prefillObjektId={m.objekt_id} prefillMieterId={m.id}
          onAbbrechen={() => setNeu(false)} onGespeichert={() => { setNeu(false); zeige('Termin angelegt'); meldeMieterGeaendert(m.id); }} />
      ) : null}
      <InnenKarte style={GLAS}>
        <KartenTitel rechts={!neu ? <ImmoButton groesse={36} icon={Plus} onClick={() => setNeu(true)}>Termin anlegen</ImmoButton> : undefined}>Anstehende Termine</KartenTitel>
        {s.daten.termine.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Keine anstehenden Termine.</span>}
        <div className="flex flex-col">
          {s.daten.termine.map(t => {
            const d = new Date(t.start_datum);
            const zeit = Number.isNaN(d.getTime()) || t.start_datum.length <= 10 ? 'ganztägig' : `${ZEIT.format(d)} Uhr`;
            return (
              <button key={t.id} type="button" onClick={() => oeffne(t.id)} title="Im Kalender öffnen" className="immo-hover-flaeche text-left cursor-pointer"
                style={{ border: 'none', background: 'transparent', borderRadius: 14, padding: 0, color: 'var(--ink)' }}>
                <PanelZeile icon={CalendarCheck} ton="acc" titel={t.titel || 'Termin'} sub={zeit} rechts={deDatum(t.start_datum)} />
              </button>
            );
          })}
        </div>
      </InnenKarte>
    </>
  );
}

// ── Tickets (Screen 34) ────────────────────────────────────────────────────────
export function TicketsPanel() {
  const s = useMieterSeite();
  const m = s.daten.mieter;
  if (!m.objekt_id) {
    return <LeerZustand icon={NotePencil} titel="Keinem Objekt zugeordnet" text="Tickets werden einem Objekt zugeordnet. Ordne den Mieter zuerst einer Einheit zu." />;
  }
  return (
    <TicketsBereich tickets={s.daten.tickets} objektId={m.objekt_id} einheitId={m.einheit_id ?? undefined} mieterId={m.id}
      onGeaendert={() => meldeMieterGeaendert(m.id)} />
  );
}

// ── Zähler (Screen 35) ─────────────────────────────────────────────────────────
export function ZaehlerPanel() {
  const s = useMieterSeite();
  const m = s.daten.mieter;
  const o = s.daten.objekt;
  return (
    <>
      {o ? (
        <ZaehlerVerwaltung zaehler={s.daten.zaehler} objekt={o} onGeaendert={() => meldeMieterGeaendert(m.id)} einheiten={s.einheit ? [s.einheit] : []}
          vorbelegung={{ einheitBezeichnung: s.einheit || undefined, mieter: { id: m.id, label: s.name, typ: 'mieter' } }}
          leerText="Für diese Einheit sind noch keine Zähler angelegt." />
      ) : (
        <LeerZustand icon={NotePencil} titel="Keinem Objekt zugeordnet" text="Zähler gehören zu einem Objekt bzw. einer Einheit." />
      )}
      <div className="flex justify-end">
        <ImmoButton icon={EnvelopeSimple} disabled title="Portal-Nachrichten werden noch angebunden">Ablesung beim Mieter anfordern</ImmoButton>
      </div>
    </>
  );
}

// ── Notizen (Screen 36) ────────────────────────────────────────────────────────
export function NotizenPanel() {
  const s = useMieterSeite();
  const m = s.daten.mieter;
  const [text, setText] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [toast, zeige] = useToastPill();
  const speichere = async () => {
    const t = text.trim();
    if (!t) return;
    setLaeuft(true);
    try {
      await speichereNotiz('mieter', m.id, t);
      setText('');
      zeige('Notiz gespeichert');
      meldeMieterGeaendert(m.id);
    } catch (err) {
      zeige(`Nicht gespeichert: ${fehlerText(err)}`, true);
    } finally {
      setLaeuft(false);
    }
  };
  const loesche = async (id: string) => {
    try {
      await loescheNotiz(id);
      zeige('Notiz gelöscht');
      meldeMieterGeaendert(m.id);
    } catch (err) {
      zeige(`Nicht gelöscht: ${fehlerText(err)}`, true);
    }
  };
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte style={GLAS}>
        <KartenTitel>Neue Notiz</KartenTitel>
        <textarea value={text} onChange={e => setText(e.target.value)} placeholder={`Notiz zu ${s.vorname} …`} rows={4} aria-label="Neue Notiz"
          style={{ width: '100%', borderRadius: 15, padding: 14, border: 'none', resize: 'vertical', background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)', fontSize: 15, color: 'var(--ink)', outline: 'none' }} />
        <div className="flex justify-end">
          <ImmoButton art="akzent" groesse={44} icon={Plus} disabled={laeuft || !text.trim()} onClick={() => { void speichere(); }}>{laeuft ? 'Wird gespeichert …' : 'Notiz speichern'}</ImmoButton>
        </div>
      </InnenKarte>
      <InnenKarte style={GLAS}>
        <KartenTitel rechts={s.daten.notizen.length ? `${s.daten.notizen.length}` : undefined}>Notizen</KartenTitel>
        {s.daten.notizen.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Keine Notizen</span>}
        <div className="flex flex-col">
          {s.daten.notizen.map(n => (
            <div key={n.id} className="flex items-start" style={{ gap: 12, padding: '10px 6px', borderBottom: '1px solid var(--line)' }}>
              <span className="flex flex-col flex-1 min-w-0" style={{ gap: 2 }}>
                <span style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{n.text}</span>
                <span className="immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)' }}>{deDatum(n.created_at)}</span>
              </span>
              <ImmoButton groesse={32} pille={false} art="bad" icon={Trash} aria-label="Notiz löschen" title="Notiz löschen" onClick={() => { void loesche(n.id); }} />
            </div>
          ))}
        </div>
        <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Abhaken (erledigt) wird noch angebunden.</span>
      </InnenKarte>
    </>
  );
}

// ── KI-Zusammenfassung (Screen 37) ─────────────────────────────────────────────
export function KiPanel() {
  const s = useMieterSeite();
  const m = s.daten.mieter;
  const fakten = kiFakten({
    vorname: s.vorname, name: s.name, objekt: s.objektName, einheit: s.einheit, beginn: s.beginn, zukunft: s.status === 'einzug', heute: s.heute,
    saldo: s.konto ? s.konto.saldo : null, offeneTickets: s.daten.tickets.filter(t => istTicketOffen(t.status)).length, letztesTicket: null,
    portalAktiv: s.portal === 'aktiv', gekuendigtZum: istGekuendigt(m.status) ? s.ende : null,
    gesamtmiete: s.miete.gesamt, kautionStatus: kautionInfo(m).status, anstehendeTermine: s.daten.termine.length,
  });
  return (
    <>
      <InnenKarte style={GLAS}>
        <div className="flex items-start" style={{ gap: 14 }}>
          <span className="flex items-center justify-center shrink-0" style={{ width: 42, height: 42, borderRadius: 14, background: 'var(--acc-soft)' }}><Sparkle weight="fill" size={20} color="var(--acc)" /></span>
          <div className="flex flex-col min-w-0" style={{ gap: 4 }}>
            <span style={{ fontSize: 15, fontWeight: 800 }}>Zusammenfassung</span>
            <span style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink2)' }}>{s.zusammenfassung}</span>
            <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Automatisch aus den Fakten erstellt – eine KI-Formulierung wird noch angebunden.</span>
          </div>
        </div>
      </InnenKarte>
      <InnenKarte style={GLAS}>
        <KartenTitel>Fakten</KartenTitel>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', columnGap: 28 }}>
          {fakten.map(f => (
            <div key={f.label} className="flex items-center justify-between" style={{ gap: 12, padding: '11px 0', borderBottom: '1px solid var(--line)' }}>
              <span style={{ fontSize: 14, color: 'var(--ink2)' }}>{f.label}</span>
              <span className="immo-zahl text-right" style={{ fontSize: 14, fontWeight: 800, color: f.ton === 'neutral' ? 'var(--ink)' : `var(--${f.ton})` }}>{f.wert}</span>
            </div>
          ))}
        </div>
      </InnenKarte>
    </>
  );
}
